import { apiClient } from './apiClient';
import { MonitorStatus } from './enums';
import { StockRanking } from '@/services/enums';

export interface StockStrategyConditions {
  minAmount?: number;
  minTurnover?: number;
  maxTurnover?: number;
  minMarketCap?: number;
  ranking?: StockRanking;
  limit?: number;
  industry?: string;
  area?: string;
}

export interface StockStrategy {
  id: number;
  strategyId: string;
  name: string;
  description?: string;
  conditions: StockStrategyConditions;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StockStrategyResult {
  symbol: string;
  name: string;
  price: number;
  change: number;
  amount: number;
  turnover: number;
  mktcap: number;
  industry: string;
  score?: number;
  peRatio?: number;
  pbRatio?: number;
  totalMktCap?: number;
  volumeRatio?: number;
}

export interface StockDataStats {
  totalStocks: number;
  syncedCount: number;
  unsyncedCount: number;
  latestDate: string | null;
  actualLatestDate?: string | null;
}

export interface OptimizedResults {
  configs: Record<string, any>;
  report: string;
  sensitivity?: Record<string, any>;
  monitors?: any[];
  positions?: any[];
  history?: any[];
  riskAnalysis?: {
    industryDistribution: Array<{ name: string; value: number; percent: string }>;
  };
  industryMap?: Record<string, string>;
  marketBreadth?: {
    timestamp: string;
    totalStocks: number;
    aboveMa20Count: number;
    aboveMa20Percent: number;
    aboveMa60Count: number;
    aboveMa60Percent: number;
    bullishMacdCount: number;
    bullishMacdPercent: number;
    breadthScore: number;
    csi300Trend: 'bull' | 'bear' | 'neutral';
  };
}

export interface TelegramAlertConfig {
  id: number;
  userId: number;
  chatId: string | null;
  isEnabled: boolean;
  alertFrequency: 'every_signal' | 'daily_summary' | 'hourly_summary';
  minSignalScore: number;
  enableBuySignals: boolean;
  enableSellSignals: boolean;
  updatedAt: string;
}

export interface AllocationSuggestion {
  symbol: string;
  name: string;
  volatility: number;
  weight: number;
  suggestedCapital: number;
  expectedAnnualReturn: number;
  maxDrawdown: number;
  strategyCode: string;
}

export interface PortfolioAllocationResult {
  totalCapital: number;
  allocations: AllocationSuggestion[];
  totalRiskScore: number;
}

export interface PortfolioBacktestResult {
  totalReturn: number;
  totalReturnPct: number;
  annualReturn: number;
  maxDrawdown: number;
  sharpeRatio: number;
  totalTrades: number;
  winRate: number;
  equityCurve: any[];
  individualPerformance: Record<string, any>;
}

export interface BrinsonAttribution {
  allocationEffect: number;
  selectionEffect: number;
  interactionEffect: number;
  totalExcessReturn: number;
}

export interface WFOWindow {
  id: number;
  trainStart: string;
  trainEnd: string;
  testStart: string;
  testEnd: string;
}

export interface WFOWindowResult {
  window: WFOWindow;
  bestParams: any;
  trainMetrics: any;
  testMetrics: any;
  riskMultiplier: number;
  brinsonAttribution?: BrinsonAttribution;
}

export interface WFOResult {
  windows: WFOWindowResult[];
  aggregatedOOSMetrics: any;
  sustainabilityScore: number;
  psr: number; // Probabilistic Sharpe Ratio
  latestSensitivity?: SensitivityResult;
  latestMonteCarlo?: MonteCarloResult;
}

export interface SensitivityVariation {
  value: any;
  metrics: any;
  score: number;
  impact: number;
}

export interface ParameterSensitivity {
  name: string;
  variations: SensitivityVariation[];
  stability: number;
}

export interface SensitivityResult {
  baseParams: any;
  baseMetrics: any;
  parameterSensitivity: ParameterSensitivity[];
  overallStabilityScore: number;
}

export interface MonteCarloResult {
  simulations: number;
  baseMetrics: any;
  percentiles: {
    p5: any;
    p50: any;
    p95: any;
  };
  failureProbability: number;
  riskOfRuin: number;
  paths?: number[][];
}

export interface StockHistorySignal {
  id: number;
  symbol: string;
  name: string;
  strategy: string;
  industry?: string;
  area?: string;
  price: number;
  change: number;
  volRatio: number;
  turnoverRate?: number;
  isConfirmed: boolean;
  triggerTime: string;
  time?: string; // 为兼容 UI 添加的字段
  reason?: string; // 信号触发原因
  metadata?: any;
  pnl1d?: number;
  pnl3d?: number;
  pnl5d?: number;
  maxPnl?: number;
  lastChecked?: string;
  nextChecked?: string;
}

export interface StockHistorySignalsResponse {
  signals: StockHistorySignal[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScoreRangeStat {
  min: number;
  max: number;
  label: string;
  total: number;
  winRate: number;
  avgPnl: number;
  avgMaxPnl: number;
}

export interface StockSignalPerformanceStats {
  total: number;
  winRate: number;
  avgPnl1d: number;
  avgMaxPnl: number;
  byScore: ScoreRangeStat[];
  synergyStats: {
    total: number;
    winRate: number;
    avgPnl: number;
  };
}

export class StockStrategyService {
  /**
   * 获取核心策略信号
   */
  async getCoreStrategySignals(params: {
    limit?: number;
    offset?: number;
    symbol?: string;
    isConfirmed?: boolean;
  }): Promise<StockHistorySignalsResponse> {
    try {
      const response = await apiClient.get<StockHistorySignalsResponse>('/stock-strategy/strategies/signals/history', {
        params,
      });

      if (!response.success) {
        throw new Error(response.message || '获取核心策略信号失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Get core strategy signals error:', error);
      throw error;
    }
  }

  /**
   * 获取 A股 历史信号记录
   */
  async getStockHistorySignals(params: {
    limit?: number;
    offset?: number;
    symbol?: string;
    strategy?: string;
    isConfirmed?: boolean;
  }): Promise<StockHistorySignalsResponse> {
    try {
      const response = await apiClient.get<StockHistorySignalsResponse>('/stock-strategy/strategies/signals/history', {
        params,
      });

      if (!response.success) {
        throw new Error(response.message || '获取 A股 历史信号记录失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Get stock history signals error:', error);
      throw error;
    }
  }

  /**
   * 获取信号绩效统计数据
   */
  async getStockSignalPerformanceStats(days: number = 30): Promise<StockSignalPerformanceStats> {
    try {
      const response = await apiClient.get<StockSignalPerformanceStats>(
        '/stock-strategy/strategies/signals/performance-stats',
        {
          params: { days },
        },
      );

      if (!response.success) {
        throw new Error(response.message || '获取信号绩效统计数据失败');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to get stock signal performance stats:', error);
      throw error;
    }
  }

  /**
   * 运行 WFO 分析
   */
  async runWFOAnalysis(config: any): Promise<WFOResult> {
    try {
      const response = await apiClient.post<WFOResult>('/stock-strategy/strategies/wfo-analysis', config);
      if (!response.success) {
        throw new Error(response.message || 'WFO 分析失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to run WFO analysis:', error);
      throw error;
    }
  }

  /**
   * 获取最新 WFO 结果
   */
  async getLatestWFOResult(): Promise<WFOResult | null> {
    try {
      const response = await apiClient.get<WFOResult>('/stock-strategy/strategies/wfo-result');
      if (!response.success) {
        return null;
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get latest WFO result:', error);
      return null;
    }
  }

  /**
   * 获取所有选股策略
   */
  async getAllStrategies(): Promise<StockStrategy[]> {
    try {
      const response = await apiClient.get<StockStrategy[]>('/stock-strategy/strategies');
      if (!response.success) {
        throw new Error(response.message || '获取策略列表失败');
      }
      return response.data || [];
    } catch (error) {
      console.error('Failed to get strategies:', error);
      throw error;
    }
  }

  /**
   * 初始化系统策略
   */
  async initStrategies(): Promise<void> {
    try {
      const response = await apiClient.post('/stock-strategy/strategies/init');
      if (!response.success) {
        throw new Error(response.message || '初始化策略失败');
      }
    } catch (error) {
      console.error('Failed to init strategies:', error);
      throw error;
    }
  }

  /**
   * 执行选股策略
   */
  async executeStrategy(strategyId: string): Promise<StockStrategyResult[]> {
    try {
      const response = await apiClient.get<StockStrategyResult[]>(`/stock-strategy/strategies/${strategyId}/execute`);
      if (!response.success) {
        throw new Error(response.message || '执行策略失败');
      }
      return response.data || [];
    } catch (error) {
      console.error('Failed to execute strategy:', error);
      throw error;
    }
  }

  /**
   * 获取股票数据完整性统计
   */
  async getStockDataStats(): Promise<StockDataStats> {
    try {
      const response = await apiClient.get<StockDataStats>('/stock-strategy/strategies/stats');
      if (!response.success) {
        throw new Error(response.message || '获取数据统计失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get stock stats:', error);
      throw error;
    }
  }

  /**
   * 获取量化优化结果
   */
  async getOptimizedResults(): Promise<OptimizedResults> {
    try {
      const response = await apiClient.get<OptimizedResults>('/stock-strategy/strategies/optimized-results');
      if (!response.success) {
        throw new Error(response.message || '获取优化结果失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get optimized results:', error);
      throw error;
    }
  }

  /**
   * 切换监控状态
   */
  async toggleMonitorStatus(monitorId: number, status: MonitorStatus): Promise<void> {
    try {
      const response = await apiClient.post(`/stock-strategy/strategies/monitors/${monitorId}/toggle`, { status });
      if (!response.success) {
        throw new Error(response.message || '操作失败');
      }
    } catch (error) {
      console.error('Failed to toggle monitor status:', error);
      throw error;
    }
  }

  /**
   * 获取 Telegram 告警配置
   */
  async getTelegramAlertConfig(): Promise<TelegramAlertConfig> {
    try {
      const response = await apiClient.get<TelegramAlertConfig>('/stock-strategy/strategies/telegram-config');
      if (!response.success) {
        throw new Error(response.message || '获取告警配置失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get telegram config:', error);
      throw error;
    }
  }

  /**
   * 更新 Telegram 告警配置
   */
  async updateTelegramAlertConfig(config: Partial<TelegramAlertConfig>): Promise<TelegramAlertConfig> {
    try {
      const response = await apiClient.post<TelegramAlertConfig>('/stock-strategy/strategies/telegram-config', config);
      if (!response.success) {
        throw new Error(response.message || '更新 Telegram 告警配置失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update telegram alert config:', error);
      throw error;
    }
  }

  /**
   * 获取资金分配建议
   */
  async getPortfolioAllocation(totalCapital: number): Promise<PortfolioAllocationResult> {
    try {
      const response = await apiClient.get<PortfolioAllocationResult>('/stock-strategy/portfolio/allocation', {
        params: { totalCapital },
      });
      if (!response.success) {
        throw new Error(response.message || '获取资金分配建议失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get portfolio allocation:', error);
      throw error;
    }
  }

  /**
   * 运行组合整体回测
   */
  async runPortfolioBacktest(initialCapital: number, lookbackYears: number): Promise<PortfolioBacktestResult> {
    try {
      const response = await apiClient.get<PortfolioBacktestResult>('/stock-strategy/portfolio/backtest', {
        params: { initialCapital, lookbackYears },
      });
      if (!response.success) {
        throw new Error(response.message || '组合回测失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to run portfolio backtest:', error);
      throw error;
    }
  }

  /**
   * 生成参数敏感度报告
   */
  async generateSensitivityReport(templateName: string, params: any): Promise<any> {
    try {
      const response = await apiClient.post<any>('/stock-strategy/strategies/sensitivity-report', {
        templateName,
        params,
      });
      if (!response.success) {
        throw new Error(response.message || '生成敏感度报告失败');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to generate sensitivity report:', error);
      throw error;
    }
  }
}

export const stockStrategyService = new StockStrategyService();
